import { React, useState, useEffect } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import { useSpring, useTransition, animated } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import { LocationCard } from '@digsup/patterns-card';
import { Button } from '@digsup/components-button';
import { Icon } from '@digsup/components-icon';
import { Card } from '@digsup/components-card';
import { Image } from '@digsup/components-image';
import { Spin } from '@digsup/components-spin';
import { List } from '@digsup/components-list';
import locationThumb from '@digsup/assets-images/src/select-location.svg';
import { MultiunitProperty } from './MultiunitProperty';

import './Property.scss';

export const Property = ({
  isMultiunit,
  onMultiunitSubmit,
  multiunitTypeOptions,
  data,
  isLoading,
  isVisible,
  isExpanded,
  onClose,
  onConfirm,
  errorMsg,
  confirmButtonText,
}) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const [dragTransform, setDragTransform] = useSpring(() => ({ y: 0 }));

  useEffect(() => {
    if (!expanded) {
      setExpanded(isExpanded);
    }
    if (dragTransform !== 0) {
      setDragTransform({ y: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const propertyTransitions = useTransition(
    !isLoading && data !== null && isVisible,
    null,
    {
      from: {
        marginTop: '0px',
        transform: 'translate3d(0,0%,0)',
      },
      enter: {
        marginTop: isMultiunit ? '0px' : '-123px',
        transform: `translate3d(0,${isMultiunit ? '-150%' : '0%'},0)`,
      },
      leave: { marginTop: '0px', transform: 'translate3d(0,0%,0)' },
      update: {
        // eslint-disable-next-line no-nested-ternary
        transform: !isMultiunit
          ? expanded
            ? 'translate3d(0,-92%,0)'
            : 'translate3d(0,0%,0)'
          : 'translate3d(0,-150%,0)',
      },
    },
  );

  const noDataTransitions = useTransition(
    !isLoading && data === null && isVisible,
    null,
    {
      from: {
        transform: 'translate3d(0,0%,0)',
      },
      enter: {
        transform: 'translate3d(0,-100%,0)',
      },
      leave: {
        transform: 'translate3d(0,0%,0)',
      },
    },
  );

  const loadingTransitions = useTransition(isLoading, null, {
    from: { transform: 'translate3d(0,0%,0)' },
    enter: { transform: 'translate3d(0,-400%,0)' },
    leave: { transform: 'translate3d(0,0%,0)' },
  });

  const bind = useDrag(({ down, movement: [, my], cancel }) => {
    setDragTransform({ y: down ? my : 0 });

    if (my > 70 && !expanded) {
      onClose();
    }

    if (my > 50 && expanded) {
      cancel();
      setExpanded(false);
    }

    if (!expanded && my < -50) {
      cancel();
      setExpanded(true);
    }
  });

  return (
    <>
      {loadingTransitions.map(({ item, key, props: { ...styles } }) =>
        item ? (
          <div className="c-map-property" key={key}>
            <animated.div style={styles}>
              <Card
                radius="medium"
                shadow="level-30"
                border="none"
                className="c-map-property__loader">
                <div className="c-map-property__spin">
                  <Spin />
                </div>
              </Card>
            </animated.div>
          </div>
        ) : null,
      )}

      <div className="c-map-property">
        <animated.div
          {...bind()}
          style={{
            zIndex: 12,
            positiom: 'relative',
            transform: dragTransform.y.interpolate(
              y => `translate3d(0,${y}px,0)`,
            ),
            touchAction: 'pan-x',
          }}>
          {noDataTransitions.map(({ item, key, props: { ...styles } }) =>
            item ? (
              <div key={key}>
                <animated.div style={styles}>
                  <Card
                    radius="medium"
                    shadow="level-30"
                    className="u-background-white c-map-property__placeholder">
                    <Button
                      onClick={onClose}
                      size="small"
                      variant="flat"
                      iconOnly
                      icon={<Icon name="x" size="small" />}
                      className="c-map-property__placeholder-toggle"
                    />
                    <List
                      display="flex"
                      nowrap
                      align="start"
                      valign="center"
                      spacing="small">
                      <List.Item>
                        <Image
                          ratio={36 / 31}
                          maxWidth="36px"
                          src={locationThumb}
                          className="u-display-block"
                          alt="No information placeholder"
                        />
                      </List.Item>
                      <List.Item>
                        <Card.Text size="small" align="center">
                          {errorMsg}
                        </Card.Text>
                      </List.Item>
                    </List>
                  </Card>
                </animated.div>
              </div>
            ) : null,
          )}
        </animated.div>
      </div>

      <div className="c-map-property">
        <animated.div
          {...bind()}
          style={{
            zIndex: 12,
            positiom: 'relative',
            transform: dragTransform.y.interpolate(
              y => `translate3d(0,${y}px,0)`,
            ),
            touchAction: 'pan-x',
          }}>
          {propertyTransitions.map(({ item, key, props: { ...styles } }) =>
            item ? (
              <animated.div
                key={key}
                style={styles}
                className="c-map-property__location-card">
                <animated.div>
                  {isMultiunit ? (
                    <MultiunitProperty
                      address={data?.address}
                      unit={data?.unit}
                      unitType={data?.unitType}
                      img={data?.img}
                      multiunitTypeOptions={multiunitTypeOptions}
                      onSubmit={onMultiunitSubmit}
                      onClose={onClose}
                    />
                  ) : (
                    <div className="c-map-property__location-card-wrapper">
                      <div className="c-map-property__btn-expand">
                        <Button
                          size="small"
                          variant="flat"
                          iconOnly
                          onClick={() => setExpanded(state => !state)}
                          icon={
                            <Icon
                              size="small"
                              name={
                                expanded
                                  ? 'arrow-simple-down'
                                  : 'arrow-simple-up'
                              }
                            />
                          }
                        />
                      </div>
                      <LocationCard
                        onClose={onClose}
                        onConfirm={onConfirm}
                        {...data}
                        confirmButtonText={confirmButtonText}
                        className={`c-map-property__location-card-inner ${expanded &&
                          'c-map-property__location-card-inner--expanded'}`}
                      />
                    </div>
                  )}
                </animated.div>
              </animated.div>
            ) : null,
          )}
        </animated.div>
      </div>
    </>
  );
};

Property.defaultProps = {
  errorMsg: 'Sorry, we don’t have information on that spot.',
};

Property.propTypes = {
  /**
   * Property type
   */
  isMultiunit: PropTypes.bool,
  /**
   * On Multiunit form submit
   */
  onMultiunitSubmit: PropTypes.func,
  /**
   * Multiunit type select options
   */
  multiunitTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  /**
   * Property data - see Location card prop
   */
  data: PropTypes.object,
  /**
   * Indicates property data loading
   */
  isLoading: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  /**
   * After clicking on map or searching a property, whether we
   * immediately expand the feature modal or not
   */
  isExpanded: PropTypes.bool.isRequired,
  /**
   * Callback for info and placeholder cards close btn click
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Callback when user clicks confirm btn
   */
  onConfirm: PropTypes.func.isRequired,
  /**
   * Indicates property error message
   */
  errorMsg: PropTypes.string,
  confirmButtonText: PropTypes.string,
};
